"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import Swal from "sweetalert2";

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [CPassword, setCPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);

  const router = useRouter();

  // CRITICAL: Clear all cookies on signup page load to prevent data leakage
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Clear all cookies
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.trim().split('=')[0];
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      });
      
      // Clear storage
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('[SignUp] Cleared all cookies and storage');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate password confirmation
    if (password !== CPassword) {
      await Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: "Passwords do not match",
        confirmButtonColor: "#284F9E",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName }),
      });

      const data = await response.json();

      if (response.ok) {
        await Swal.fire({
          icon: "success",
          title: "Registration Successful",
          text: "Please sign in to continue!",
          confirmButtonColor: "#284F9E",
          timer: 1500,
          timerProgressBar: true,
        });
        router.push("/auth/signin");
      } else {
        await Swal.fire({
          icon: "error",
          title: "Registration Failed",
          text: data.error || "An error occurred during registration",
          confirmButtonColor: "#284F9E",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: "An unexpected error occurred",
        confirmButtonColor: "#284F9E",
      });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen mx-auto bg-gray-100 text-gray-900 flex justify-center">
      <div className="flex-1 bg-[#284F9E] hidden lg:flex flex-col justify-around">
        <div className="flex flex-col justify-center ml-20 w-[80%] bg-contain bg-center bg-no-repeat">
          <div className="mb-10">
            <svg
              width="91"
              height="101"
              viewBox="0 0 91 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M54.5023 44.0182C58.0706 44.0671 60.9546 42.6487 63.1542 39.8608C64.8162 37.8066 65.7938 35.1165 66.4781 32.2797C66.8692 30.5679 67.5046 26.5083 67.5046 26.5083H27.911L11.8291 100.95H25.2714L37.6383 43.9693C37.5894 43.9693 51.1295 44.0182 54.5023 44.0182Z"
                fill="white"
              />
              <path
                d="M87.8881 13.6949C84.7109 6.21162 79.6761 1.90751 72.8328 0.78257C70.2421 0.342376 67.7003 0 65.0607 0C48.49 0 13.6378 0 13.6378 0L0 62.9966H13.3934L23.4629 16.5806H31.5772H69.0689C73.6149 16.5317 77.5742 19.8087 77.1343 26.1182C76.6944 31.6939 75.4724 37.0252 73.3705 42.1119C72.5395 44.1172 70.9264 46.4649 68.4824 49.155C65.4517 52.432 61.6879 54.0949 57.142 54.0949C55.2845 54.0949 45.215 54.046 45.215 54.046L34.9988 101H48.3434L54.9423 70.3821H58.7062C59.586 70.3821 60.4659 70.3332 61.2968 70.1375C75.3257 66.7138 85.0042 54.8775 88.4258 37.3186C89.2568 33.0145 89.7456 30.3734 89.8923 29.3462C90.5766 23.9661 89.8923 18.7816 87.8881 13.6949Z"
                fill="white"
              />
            </svg>
          </div>
          <div className="text-white text-[65px] leading-[1.1] font-semibold">
            Get Started with Pixcon
          </div>
        </div>
        <p className="ml-20 text-[14px] text-white">
          © 2024 Pixcon. All Rights Reserved.
        </p>
      </div>
      <div className="lg:w-1/2 w-full px-10 py-16 flex justify-center">
        <div className="flex flex-col w-[100%] justify-center">
          <div className="mx-auto w-[100%]">
            <div>
              <svg
                width="136"
                height="41"
                viewBox="0 0 136 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_283_16911)">
                  <path
                    d="M85.9037 23.5848L79.7649 23.8818C79.7918 23.4768 79.8053 23.1528 79.8053 22.9368C79.8053 21.5867 79.468 20.4392 78.7799 19.4942C78.0918 18.5492 77.0664 18.0767 75.6903 18.0767C73.4371 18.0767 71.818 19.4267 70.8466 22.1402C69.8752 24.8538 69.376 27.4998 69.376 30.0918C69.376 32.0224 69.7942 33.4264 70.6172 34.3174C71.4403 35.2084 72.5061 35.6404 73.8014 35.6404C76.4323 35.6404 78.2403 33.9934 79.2252 30.6859L84.7434 31.6444C82.6926 37.7599 78.8879 40.811 73.3157 40.811C69.8752 40.811 67.2307 39.677 65.4093 37.3954C63.5879 35.1274 62.6704 32.3734 62.6704 29.1738C62.6704 24.8943 63.8577 21.1142 66.2323 17.8607C68.6069 14.6072 71.7506 12.9736 75.6903 12.9736C78.5371 12.9736 80.9117 13.8376 82.8276 15.5657C84.7704 17.2937 85.7823 19.9667 85.9037 23.5848Z"
                    fill="#1F448E"
                  />
                  <path
                    d="M97.251 40.9999C94.0534 40.9999 91.4764 39.9198 89.5201 37.7598C87.5637 35.5998 86.5923 32.7513 86.5923 29.2007C86.5923 24.9077 87.7526 21.1411 90.0597 17.9011C92.3804 14.661 95.4566 13.041 99.2883 13.041C102.58 13.041 105.198 14.1345 107.141 16.3216C109.084 18.5086 110.041 21.3976 110.041 25.0022C110.041 29.3627 108.895 33.1158 106.588 36.2748C104.294 39.4203 101.177 40.9999 97.251 40.9999ZM97.3455 35.9913C99.6796 35.9913 101.339 34.6548 102.351 31.9817C103.363 29.3087 103.862 26.6762 103.862 24.1112C103.862 20.0746 102.351 18.0496 99.3153 18.0496C97.1566 18.0496 95.5375 19.3051 94.4582 21.8296C93.3788 24.3542 92.8256 27.1082 92.8256 30.1187C92.8256 34.0338 94.3367 35.9913 97.3455 35.9913Z"
                    fill="#1F448E"
                  />
                  <path
                    d="M136 13.1357L131.318 40.9731H126.11L118.001 20.8038C117.947 22.1539 117.502 25.3264 116.679 30.335L114.912 40.9731H109.677L114.318 13.1357H121.374L128.066 30.1054C128.107 29.5519 128.512 26.8249 129.294 21.9378L130.765 13.1357H136Z"
                    fill="#1F448E"
                  />
                  <path
                    d="M34.5935 12.8657L30.6269 40.6491H25.1626L29.1293 12.8657H34.5935Z"
                    fill="#2F4F9E"
                  />
                  <path
                    d="M49.6101 20.5202L48.6522 19.0082C47.2895 16.8617 44.7665 12.8792 44.7665 12.8792H36.1855L49.6236 32.9539L69.8347 0L49.6101 20.5202Z"
                    fill="#58BAE9"
                  />
                  <path
                    d="M43.8222 27.1084L34.6611 40.6491H43.9976L48.0722 34.088L43.8222 27.1084Z"
                    fill="#2F4F9E"
                  />
                  <path
                    d="M55.0744 27.0947L50.8379 34.0743L54.9665 40.6489H64.249L55.0744 27.0947Z"
                    fill="#2F4F9E"
                  />
                  <path
                    d="M19.0779 17.3613H8.72949C11.5898 17.3613 16.5684 17.3748 19.0779 17.3613Z"
                    fill="#58BAE9"
                  />
                  <path
                    d="M15.0437 24.9346C16.0286 24.9481 16.8247 24.5566 17.4318 23.7871C17.8905 23.2201 18.1604 22.4776 18.3493 21.6946C18.4572 21.2221 18.6326 20.1016 18.6326 20.1016H7.70403L3.26514 40.6488H6.97545L10.3889 24.9211C10.3755 24.9211 14.1128 24.9346 15.0437 24.9346Z"
                    fill="#58BAE9"
                  />
                  <path
                    d="M24.2587 16.5647C23.3817 14.4992 21.9921 13.3112 20.1032 13.0007C19.3881 12.8792 18.6865 12.7847 17.9579 12.7847C13.3841 12.7847 3.76429 12.7847 3.76429 12.7847L0 30.1729H3.69683L6.47619 17.3612H8.71587H19.0643C20.319 17.3477 21.4119 18.2522 21.2905 19.9938C21.169 21.5328 20.8317 23.0043 20.2516 24.4083C20.0222 24.9618 19.577 25.6098 18.9024 26.3523C18.0659 27.2568 17.027 27.7158 15.7722 27.7158C15.2595 27.7158 12.4802 27.7023 12.4802 27.7023L9.66032 40.6625H13.3437L15.1651 32.2114H16.204C16.4468 32.2114 16.6897 32.1979 16.919 32.1439C20.7913 31.1989 23.4627 27.9319 24.4071 23.0853C24.6365 21.8973 24.7714 21.1683 24.8119 20.8848C25.0008 19.3997 24.8119 17.9687 24.2587 16.5647Z"
                    fill="#58BAE9"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_283_16911">
                    <rect width="136" height="41" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>

            <h1 className="text-2xl pt-10 text-[30px] font-extrabold">
              Sign Up
            </h1>

            <form onSubmit={handleSubmit}>
              <div className="flex flex-row gap-7 mt-12">
                <div className="mb-6 w-[50%]">
                  <label
                    htmlFor="firstName"
                    className="block mb-2 text-sm font-medium text-black"
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    className="bg-white border border-[#E1E1E1] text-black text-sm rounded-md block w-full p-4 outline-none"
                    placeholder="Arafat"
                    required
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="mb-6 w-[50%]">
                  <label
                    htmlFor="lastName"
                    className="block mb-2 text-sm font-medium text-black"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="bg-white border border-[#E1E1E1] text-black text-sm rounded-md block w-full p-4 outline-none"
                    placeholder="Ovi"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="email"
                  className="block mb-2 text-sm font-medium text-black"
                >
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  className="bg-white border border-[#E1E1E1] text-black text-sm rounded-md block w-full p-4 outline-none"
                  placeholder="john.doe@company.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="mb-6">
                <label
                  htmlFor="password"
                  className="block mb-2 text-sm font-medium text-black"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  className="bg-white border border-[#E1E1E1] text-black text-sm rounded-md block w-full p-4 outline-none"
                  placeholder="Minimum 8 character"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="mb-6">
                <label
                  htmlFor="confirmPassword"
                  className="block mb-2 text-sm font-medium text-black"
                >
                  Retype Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="bg-white border border-[#E1E1E1] text-black text-sm rounded-md block w-full p-4 outline-none"
                  placeholder="Minimum 8 character"
                  required
                  value={CPassword}
                  onChange={(e) => setCPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-5 tracking-wide font-semibold bg-[#284F9E] text-gray-100 w-full py-4 rounded-lg transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="ml-3">
                  {loading ? "Creating account..." : "Register"}
                </span>
              </button>
            </form>

            <p className="mt-6 text-xs text-gray-600 text-center">
              Already have an account{" "}
              <Link
                href={"/auth/signin"}
                className="border-b text-black font-semibold border-gray-500 border-dotted"
              >
                Login!
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};